import { type AxiosError } from "axios";
import useSWR from "swr";
import type { KeyedMutator } from "swr";
import type { Order } from "@/features/dmManagement/types";
import { swrErrorHandler } from "@/features/dmManagement/api/handleApiError";
import { useApiClient } from "@/hooks/useApiClient";

interface UseOrderReturn {
  order: Order | undefined;
  isLoading: boolean;
  error: AxiosError | undefined;
  mutate: KeyedMutator<Order>;
}

/**
 * 注文詳細を取得するカスタムフック。
 * idの有無でconditional fetchingを実現する。
 * GetOrderAPIはidを必須引数としておりconditional fetchingに対応していないため、useSWRを直接使用する。
 */
export const useOrder = (id?: number): UseOrderReturn => {
  const { apiClient } = useApiClient();

  const fetcher = async (url: string): Promise<Order> => {
    const { data } = await apiClient.get<Order>(url);
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<Order, AxiosError>(
    id != null ? [`/dm/orders/${id}/`] : null,
    async ([url]: [string]) => await fetcher(url),
    {
      revalidateOnFocus: false,
      onError: swrErrorHandler,
    }
  );

  return {
    order: data,
    isLoading,
    error,
    mutate,
  };
};
